export const CHOREO_ANNOTATIONS = {
  PROJECT: 'openchoreo.io/project',
  DEPLOYMENT: 'openchoreo.io/deployment',
  ENDPOINT: 'openchoreo.io/endpoint',
  ENVIRONMENT: 'openchoreo.io/environment',
  ENVIRONMENT_UID: 'openchoreo.io/environment-uid',
  COMPONENT: 'openchoreo.io/component',
  COMPONENT_UID: 'openchoreo.io/component-uid',
  BRANCH: 'openchoreo.io/branch',
  WORKLOAD: 'openchoreo.io/workload',
  WORKLOAD_TYPE: 'openchoreo.io/workload-type',
  NAMESPACE: 'openchoreo.io/namespace',
  CREATED_AT: 'openchoreo.io/created-at',
  STATUS: 'openchoreo.io/status',
  PROJECT_ID: 'openchoreo.io/project-id',
  PROJECT_UID: 'openchoreo.io/project-uid',
  COMPONENT_TYPE: 'openchoreo.io/component-type',
  COMPONENT_TYPE_KIND: 'openchoreo.io/component-type-kind',
  RESOURCE: 'openchoreo.io/resource',
  RESOURCE_UID: 'openchoreo.io/resource-uid',
  RESOURCE_TYPE: 'openchoreo.io/resource-type',
  RESOURCE_TYPE_KIND: 'openchoreo.io/resource-type-kind',
  PROJECT_TYPE: 'openchoreo.io/project-type',
  PROJECT_TYPE_KIND: 'openchoreo.io/project-type-kind',
  ENDPOINT_NAME: 'openchoreo.io/endpoint-name',
  ENDPOINT_TYPE: 'openchoreo.io/endpoint-type',
  ENDPOINT_PORT: 'openchoreo.io/endpoint-port',
  ENDPOINT_VISIBILITY: 'openchoreo.io/endpoint-visibility',
  // Component Type Definition (CTD) annotations
  CTD_NAME: 'openchoreo.io/ctd-name',
  CTD_DISPLAY_NAME: 'openchoreo.io/ctd-display-name',
  CTD_GENERATED: 'openchoreo.io/ctd-generated',
  CTD_KIND: 'openchoreo.io/ctd-kind',
  // Points the sync at a hand-authored scaffolder Template to serve instead of
  // the generated wizard for this (Cluster)ComponentType.
  SCAFFOLD_TEMPLATE_URL: 'scaffolder.openchoreo.dev/backstage-template-url',
  // (Cluster)ResourceType Definition (RTD) annotations
  // Umbrella prefix `RTD` mirrors `CTD` on the Component side: a third
  // letter (`D` for Definition) keeps it distinct from `RT` (ResourceType)
  // and `CRT` (ClusterResourceType). The kind itself is carried in RTD_KIND.
  RTD_NAME: 'openchoreo.io/rtd-name',
  RTD_DISPLAY_NAME: 'openchoreo.io/rtd-display-name',
  RTD_GENERATED: 'openchoreo.io/rtd-generated',
  RTD_KIND: 'openchoreo.io/rtd-kind',
  // (Cluster)ProjectType Definition (PTD) annotations
  // Umbrella prefix `PTD` mirrors `RTD`/`CTD`: marks scaffolder Templates
  // generated per (Cluster)ProjectType so the Project-creation browse view
  // (`?view=projects`) can list them. The kind is carried in PTD_KIND.
  PTD_NAME: 'openchoreo.io/ptd-name',
  PTD_DISPLAY_NAME: 'openchoreo.io/ptd-display-name',
  PTD_GENERATED: 'openchoreo.io/ptd-generated',
  PTD_KIND: 'openchoreo.io/ptd-kind',
  // Deletion tracking
  DELETION_TIMESTAMP: 'openchoreo.io/deletion-timestamp',
  // Agent connection status
  AGENT_CONNECTED: 'openchoreo.io/agent-connected',
  AGENT_CONNECTED_COUNT: 'openchoreo.io/agent-connected-count',
  AGENT_LAST_HEARTBEAT: 'openchoreo.io/agent-last-heartbeat',
  AGENT_LAST_CONNECTED: 'openchoreo.io/agent-last-connected',
  AGENT_LAST_DISCONNECTED: 'openchoreo.io/agent-last-disconnected',
  AGENT_MESSAGE: 'openchoreo.io/agent-message',
  // Observability
  OBSERVABILITY_PLANE_REF: 'openchoreo.io/observability-plane-ref',
  OBSERVER_URL: 'openchoreo.io/observer-url',
  // Data plane reference kind (DataPlane vs ClusterDataPlane)
  DATA_PLANE_REF_KIND: 'openchoreo.io/data-plane-ref-kind',
  // Workflow plane reference (on Workflow/ClusterWorkflow entities)
  WORKFLOW_PLANE_REF: 'openchoreo.io/workflow-plane-ref',
  WORKFLOW_PLANE_REF_KIND: 'openchoreo.io/workflow-plane-ref-kind',
  // Workflow parameters schema
  WORKFLOW_PARAMETERS: 'openchoreo.dev/component-workflow-parameters',
  // Backstage ownership annotation (read from OpenChoreo CR annotations during catalog sync)
  BACKSTAGE_OWNER: 'backstage.io/owner',
} as const;

export const CHOREO_LABELS = {
  MANAGED: 'openchoreo.io/managed',
  SYSTEM: 'openchoreo.io/system',
  WORKFLOW_PROJECT: 'openchoreo.dev/project',
  WORKFLOW_COMPONENT: 'openchoreo.dev/component',
  WORKFLOW_TYPE: 'openchoreo.dev/workflow-type',
  // Marks a SecretReference's category (e.g. git credentials).
  SECRET_TYPE: 'openchoreo.dev/secret-type',
} as const;

/**
 * Value set on the {@link CHOREO_LABELS.SECRET_TYPE} label to mark a
 * SecretReference as holding git credentials. Workflows and CI build dialogs
 * discover git secrets by this label.
 */
export const GIT_SECRET_TYPE_VALUE = 'git-credentials';

/**
 * Value set on the {@link CHOREO_LABELS.SECRET_TYPE} label to mark a
 * SecretReference as a general-purpose secret. Stamped on every secret
 * created via the generic Secret API that isn't another known category.
 */
export const GENERIC_SECRET_TYPE_VALUE = 'generic';

/**
 * Custom relation types for OpenChoreo entities.
 * These extend the standard Backstage relations.
 */

/**
 * A relation indicating that a DeploymentPipeline deploys to an Environment.
 * The source is the pipeline, the target is the environment.
 */
export const RELATION_DEPLOYS_TO = 'deploysTo';

/**
 * A relation indicating that an Environment receives deployments from a DeploymentPipeline.
 * This is the inverse of RELATION_DEPLOYS_TO.
 */
export const RELATION_DEPLOYED_BY = 'deployedBy';

/**
 * A relation indicating that a System (Project) uses a DeploymentPipeline.
 * The source is the project, the target is the pipeline.
 */
export const RELATION_USES_PIPELINE = 'usesPipeline';

/**
 * A relation indicating that a DeploymentPipeline is used by a System (Project).
 * This is the inverse of RELATION_USES_PIPELINE.
 */
export const RELATION_PIPELINE_USED_BY = 'pipelineUsedBy';

/**
 * A relation indicating that an Environment is hosted on a DataPlane.
 * The source is the environment, the target is the dataplane.
 */
export const RELATION_HOSTED_ON = 'hostedOn';

/**
 * A relation indicating that a DataPlane hosts Environments.
 * This is the inverse of RELATION_HOSTED_ON.
 */
export const RELATION_HOSTS = 'hosts';

/**
 * A relation indicating that a DataPlane/WorkflowPlane is observed by an ObservabilityPlane.
 * The source is the DataPlane/WorkflowPlane, the target is the ObservabilityPlane.
 */
export const RELATION_OBSERVED_BY = 'observedBy';

/**
 * A relation indicating that an ObservabilityPlane observes DataPlanes/WorkflowPlanes.
 * This is the inverse of RELATION_OBSERVED_BY.
 */
export const RELATION_OBSERVES = 'observes';

/**
 * A relation indicating that a Workflow or ClusterWorkflow builds on a WorkflowPlane or ClusterWorkflowPlane.
 * The source is the workflow, the target is the workflow plane.
 */
export const RELATION_BUILDS_ON = 'buildsOn';

/**
 * A relation indicating that a WorkflowPlane or ClusterWorkflowPlane is used by a Workflow or ClusterWorkflow.
 * This is the inverse of RELATION_BUILDS_ON.
 */
export const RELATION_BUILDS = 'builds';

/**
 * A relation indicating that a Component is an instance of a ComponentType.
 * The source is the Component, the target is the ComponentType.
 */
export const RELATION_INSTANCE_OF = 'instanceOf';

/**
 * A relation indicating that a ComponentType has a Component instance.
 * This is the inverse of RELATION_INSTANCE_OF.
 */
export const RELATION_HAS_INSTANCE = 'hasInstance';

/**
 * A relation indicating that a ComponentType uses a ComponentWorkflow (via allowedWorkflows).
 * The source is the ComponentType, the target is the ComponentWorkflow.
 */
export const RELATION_USES_WORKFLOW = 'usesWorkflow';

/**
 * A relation indicating that a ComponentWorkflow is used by a ComponentType.
 * This is the inverse of RELATION_USES_WORKFLOW.
 */
export const RELATION_WORKFLOW_USED_BY = 'workflowUsedBy';

/**
 * A relation indicating that an Environment sends alerts to an
 * ObservabilityAlertsNotificationChannel. The source is the environment,
 * the target is the notification channel.
 */
export const RELATION_NOTIFIES = 'notifies';

/**
 * A relation indicating that an ObservabilityAlertsNotificationChannel
 * receives alerts from an Environment. This is the inverse of
 * RELATION_NOTIFIES.
 */
export const RELATION_NOTIFIED_BY = 'notifiedBy';
